/**
 * useRoomRecognition Hook
 *
 * Custom React hook for managing room recognition data.
 * Follows the same pattern as useMuseumObjects.js
 */

import { useState, useCallback, useEffect } from 'react';
import {
  addRoom,
  updateRoomImages,
  deleteRoom,
  getVenueRooms,
  getStats,
  testRecognition,
  refreshCache
} from '../RoomRecognition/utils/roomsDatabase';

/**
 * Hook for managing room recognition for a specific museum/guide
 *
 * @param {string} museumId - The museum/venue/guide ID
 * @param {string} museumName - The museum/venue name (for display)
 */
export const useRoomRecognition = (museumId, museumName = '') => {
  // State
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================================================
  // FETCH OPERATIONS
  // ============================================================================

  /**
   * Fetch all rooms for the current museum
   */
  const fetchRooms = useCallback(async () => {
    if (!museumId) {
      setRooms([]);
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[useRoomRecognition] Fetching rooms for museumId:', museumId, 'museumName:', museumName);
      const fetchedRooms = await getVenueRooms(museumName || museumId, museumId);
      console.log('[useRoomRecognition] Fetched rooms:', fetchedRooms.length, fetchedRooms);
      setRooms(fetchedRooms);
      return fetchedRooms;
    } catch (err) {
      console.error('[useRoomRecognition] Error fetching rooms:', err);
      setError(err.message || 'Failed to fetch rooms');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [museumId]);

  /**
   * Fetch system statistics
   */
  const fetchStats = useCallback(async () => {
    try {
      const fetchedStats = await getStats(museumName || museumId);
      setStats(fetchedStats);
      return fetchedStats;
    } catch (err) {
      console.error('[useRoomRecognition] Error fetching stats:', err);
      // Don't set error for stats - it's not critical
      return null;
    }
  }, [museumId, museumName]);

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new room with reference images
   *
   * @param {Object} roomData - Room data (roomName, stopId, etc.)
   * @param {File[]} imageFiles - Reference image files
   * @param {Object} metadata - Additional metadata
   */
  const createRoom = useCallback(async (roomData, imageFiles, metadata = {}) => {
    if (!museumId) {
      throw new Error('Museum ID is required');
    }

    setIsSaving(true);
    setError(null);

    try {
      // Generate room ID if not provided
      const roomId = roomData.roomId || `${museumId}_room_${Date.now()}`;

      const result = await addRoom(
        {
          roomId,
          venueId: museumId,
          venueName: museumName || roomData.venueName || museumId,
          roomName: roomData.roomName,
          audioGuideId: roomData.audioGuideId || museumId,
          stopId: roomData.stopId
        },
        imageFiles,
        metadata
      );

      // Refresh the rooms list
      await fetchRooms();

      return result;
    } catch (err) {
      console.error('[useRoomRecognition] Error creating room:', err);
      setError(err.message || 'Failed to create room');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [museumId, museumName, fetchRooms]);

  /**
   * Add more reference images to an existing room
   */
  const addRoomImages = useCallback(async (roomId, imageFiles) => {
    if (!museumId) {
      throw new Error('Museum ID is required');
    }

    setIsSaving(true);
    setError(null);

    try {
      const result = await updateRoomImages(roomId, museumName || museumId, museumId, imageFiles);

      // Refresh the rooms list
      await fetchRooms();

      return result;
    } catch (err) {
      console.error('[useRoomRecognition] Error adding room images:', err);
      setError(err.message || 'Failed to add images');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [museumId, fetchRooms]);

  /**
   * Delete a room
   */
  const removeRoom = useCallback(async (roomId) => {
    setIsSaving(true);
    setError(null);

    try {
      await deleteRoom(roomId, museumName || museumId);

      // Update local state
      setRooms(prev => prev.filter(r => r.roomId !== roomId));

      // Clear current room if it was deleted
      if (currentRoom?.roomId === roomId) {
        setCurrentRoom(null);
      }

      return { success: true };
    } catch (err) {
      console.error('[useRoomRecognition] Error deleting room:', err);
      setError(err.message || 'Failed to delete room');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [currentRoom, museumId, museumName]);

  // ============================================================================
  // TESTING OPERATIONS
  // ============================================================================

  /**
   * Test room recognition with an image
   */
  const testRoomRecognition = useCallback(async (imageFile) => {
    setIsLoading(true);
    setError(null);
    setTestResult(null);

    try {
      const result = await testRecognition(imageFile, museumName || museumId, museumId);
      setTestResult(result);
      return result;
    } catch (err) {
      console.error('[useRoomRecognition] Test recognition error:', err);
      setError(err.message || 'Recognition test failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [museumId, museumName]);

  /**
   * Clear test result
   */
  const clearTestResult = useCallback(() => {
    setTestResult(null);
  }, []);

  /**
   * Refresh server-side cache
   */
  const refreshServerCache = useCallback(async () => {
    try {
      return await refreshCache(museumName || museumId);
    } catch (err) {
      console.error('[useRoomRecognition] Cache refresh error:', err);
      throw err;
    }
  }, [museumId, museumName]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Fetch rooms when museum changes
  useEffect(() => {
    if (museumId) {
      fetchRooms();
    } else {
      setRooms([]);
      setCurrentRoom(null);
    }
  }, [museumId, fetchRooms]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const roomCount = rooms.length;
  const hasRooms = roomCount > 0;

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    rooms,
    currentRoom,
    isLoading,
    isSaving,
    error,
    stats,
    testResult,

    // Computed
    roomCount,
    hasRooms,

    // Actions
    setCurrentRoom,
    clearError,
    clearTestResult,

    // Fetch operations
    fetchRooms,
    fetchStats,

    // CRUD operations
    createRoom,
    addRoomImages,
    removeRoom,

    // Testing
    testRoomRecognition,
    refreshServerCache
  };
};

export default useRoomRecognition;
